import get from 'lodash/get';
import { useState } from 'react';

import { useSelectCutoutLabor } from '~/api/easy-estimator-api/cutout-labor-api/hooks/useCutoutLabor';
import useAppliance from '~/core/hooks/useAppliance';
import { useRegisterUserAction } from '~/core/hooks/useRegisterUserAction';
import { ApplianceModel, ApplianceType } from '~/core/models';
import { ApplianceQtyType, ApplianceRecord } from '~/core/types/appliances';
import { getProductDescription, imgUrl, isRange } from '~/core/utils';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  decreaseCookingAppliance,
  increaseCookingAppliance,
  resetCookingApplianceById,
  resetSelectedCookingAndRanges,
  resetSelectedCookingApplianceId,
  selectSelectedAppliance,
  selectSelectedCookingApplianceId,
} from '~/redux/slices/cookingAppliance';
import {
  decreaseApplianceCutouts,
  increaseApplianceCutouts,
  resetAllCookingApplianceCutouts,
  resetCookingApplianceCutoutById,
} from '~/redux/slices/cutout';

interface ApplianceDisplayData {
  appliance: ApplianceModel;
  image: string;
  isSelected: boolean;
  qty: number;
  title: string;
}

interface UseCookingApplianceReturn {
  decreaseQuantityHandler: (appliance: ApplianceModel) => () => void;
  increaseQuantityHandler: (appliance: ApplianceModel) => () => void;
  populateApplianceDisplayData: (
    appliance: ApplianceModel
  ) => ApplianceDisplayData;
  applianceToBeRemoved: ApplianceModel | undefined;
  removeAllCookingAppliances: () => void;
  resetCookingAppliance: (appliance: ApplianceModel) => void;
  setApplianceToBeRemoved: (show: ApplianceModel | undefined) => void;
  selectedCooktops: ApplianceRecord;
  selectedRanges: ApplianceRecord;
  setShowRemoveAllAppliancesModal: (show: boolean) => void;
  setShowRemoveApplianceModal: (show: boolean) => void;
  showRemoveAllAppliancesModal: boolean;
  showRemoveApplianceModal: boolean;
  totalCookingAppliancesAmount: number;
  updateQuantityHandler: (appliance: ApplianceModel) => (qty: number) => void;
}

export const useCookingAppliances = (): UseCookingApplianceReturn => {
  const dispatch = useAppDispatch();
  const { cooktopCutout } = useSelectCutoutLabor();
  const { registerUserApplianceAction } = useRegisterUserAction();
  const { deleteAllAppliancesByType, deleteAppliancesById } = useAppliance();

  const selectedRanges = useAppSelector(
    selectSelectedAppliance('selectedRangeApplianceProduct')
  ).selected;
  const selectedCooktops = useAppSelector(
    selectSelectedAppliance('selectedCooktopApplianceProduct')
  ).selected;

  const selectedCookingApplianceId = useAppSelector(
    selectSelectedCookingApplianceId
  );

  const [showRemoveApplianceModal, setShowRemoveApplianceModal] =
    useState(false);
  const [showRemoveAllAppliancesModal, setShowRemoveAllAppliancesModal] =
    useState(false);
  const [applianceToBeRemoved, setApplianceToBeRemoved] = useState<
    ApplianceModel | undefined
  >(undefined);

  const totalCookingAppliancesAmount = Object.values({
    ...selectedCooktops,
    ...selectedRanges,
  }).reduce(
    (acc, applianceRecord) =>
      acc + ((applianceRecord.onShape ?? 0) + (applianceRecord.onCard ?? 0)),
    0
  );

  const isApplianceRange = (appliance: ApplianceModel): boolean =>
    isRange({ applianceType: appliance.applianceType }) ||
    isRange({ applianceType: appliance.type }) ||
    isRange({ applianceType: appliance.consumerDesc });

  const getQty = (
    appliance: ApplianceModel,
    qtyType?: ApplianceQtyType
  ): number => {
    const isRange = isApplianceRange(appliance);
    const selectedAppliance = isRange ? selectedRanges : selectedCooktops;
    const counter = selectedAppliance[appliance.productId];

    return qtyType
      ? get(counter, qtyType, 0)
      : (counter?.onShape ?? 0) + (counter?.onCard ?? 0);
  };

  const getSelectedApplianceType = (
    appliance: ApplianceModel
  ): 'selectedRangeApplianceProduct' | 'selectedCooktopApplianceProduct' =>
    isApplianceRange(appliance)
      ? 'selectedRangeApplianceProduct'
      : 'selectedCooktopApplianceProduct';

  const updateOperationHandler = (
    appliance: ApplianceModel,
    operation: 'ADD' | 'REMOVE',
    qtyType: ApplianceQtyType = ApplianceQtyType.onCard
  ) => {
    const [updateSelectedAppliance, updateApplianceCutouts] =
      operation === 'ADD'
        ? [increaseCookingAppliance, increaseApplianceCutouts]
        : [decreaseCookingAppliance, decreaseApplianceCutouts];

    dispatch(
      updateSelectedAppliance({
        selectedType: getSelectedApplianceType(appliance),
        applianceId: appliance.productId,
        qtyType,
      })
    );

    // Handle cutouts for cooktops (not ranges) regardless of qtyType
    // This ensures both drag-and-drop (onShape) and button clicks (onCard) get cutouts
    if (cooktopCutout && !isApplianceRange(appliance)) {
      dispatch(
        updateApplianceCutouts({
          cutoutId: cooktopCutout.productId,
          applianceId: appliance.productId,
        })
      );
    }

    if (qtyType === ApplianceQtyType.onShape) {
      deleteAppliancesById(appliance.productId, 1);
    }

    registerUserApplianceAction({ appliance });
  };

  const increaseQuantityHandler = (appliance: ApplianceModel) => () =>
    updateOperationHandler(appliance, 'ADD');

  const decreaseQuantityHandler = (appliance: ApplianceModel) => () => {
    const onCardQty = getQty(appliance, ApplianceQtyType.onCard);
    const onShapeQty = getQty(appliance, ApplianceQtyType.onShape);

    // In case there are no products on the card, confirm the removal with the modal
    if (onShapeQty >= 1 && onCardQty === 0) {
      setShowRemoveApplianceModal(true);
    } else {
      updateOperationHandler(
        appliance,
        'REMOVE',
        onCardQty === 0 ? ApplianceQtyType.onShape : undefined
      );
    }
  };

  const removeAllCookingAppliances = () => {
    if (cooktopCutout?.productId) {
      dispatch(
        resetAllCookingApplianceCutouts({
          cooktopCutoutId: cooktopCutout.productId,
        })
      );
    }

    dispatch(resetSelectedCookingAndRanges());
    dispatch(resetSelectedCookingApplianceId());

    deleteAllAppliancesByType([ApplianceType.COOKTOP, ApplianceType.RANGE]);
  };

  const resetCookingAppliance = (appliance: ApplianceModel) => {
    const selectedType = getSelectedApplianceType(appliance);

    if (
      cooktopCutout?.productId &&
      appliance.productId !== cooktopCutout.productId
    ) {
      dispatch(
        resetCookingApplianceCutoutById({
          applianceId: appliance.productId,
          cooktopCutoutId: cooktopCutout.productId,
        })
      );
    }

    dispatch(
      resetCookingApplianceById({
        selectedType,
        applianceIdToRemove: appliance.productId,
      })
    );
    deleteAppliancesById(appliance.productId);
    dispatch(resetSelectedCookingApplianceId());
  };

  const updateQuantityHandler =
    (appliance: ApplianceModel) => (newQtyValue: number) => {
      const onCardQty = getQty(appliance, ApplianceQtyType.onCard);
      const onShapeQty = getQty(appliance, ApplianceQtyType.onShape);
      const currentTotalQty = onCardQty + onShapeQty;

      if (newQtyValue === currentTotalQty) {
        return;
      }

      if (newQtyValue > currentTotalQty) {
        increaseToTargetQty(appliance, newQtyValue - currentTotalQty);

        return;
      }

      decreaseToTargetQty(appliance, onCardQty, currentTotalQty, newQtyValue);
    };

  const increaseToTargetQty = (appliance: ApplianceModel, qtyToAdd: number) => {
    Array.from({ length: qtyToAdd }).forEach(() =>
      increaseQuantityHandler(appliance)()
    );
  };

  const decreaseToTargetQty = (
    appliance: ApplianceModel,
    onCardQty: number,
    currentTotalQty: number,
    newQtyValue: number
  ) => {
    const qtyToBeRemoved = currentTotalQty - newQtyValue;
    const qtyToBeRemovedFromShape = qtyToBeRemoved - onCardQty;

    if (qtyToBeRemoved <= onCardQty) {
      Array.from({ length: qtyToBeRemoved }).forEach(() =>
        decreaseQuantityHandler(appliance)()
      );
    } else if (qtyToBeRemoved > onCardQty && onCardQty > 0) {
      Array.from({ length: onCardQty }).forEach(() =>
        decreaseQuantityHandler(appliance)()
      );
    }

    if (qtyToBeRemovedFromShape > 0) {
      setShowRemoveApplianceModal(true);
    }
  };

  const populateApplianceDisplayData = (
    appliance: ApplianceModel
  ): ApplianceDisplayData => ({
    appliance,
    qty: getQty(appliance),
    title: getProductDescription(appliance),
    image: imgUrl(appliance.beautyShotImageUrl || appliance.imageUrl, 300),
    isSelected: appliance.productId === selectedCookingApplianceId,
  });

  return {
    decreaseQuantityHandler,
    increaseQuantityHandler,
    populateApplianceDisplayData,
    applianceToBeRemoved,
    removeAllCookingAppliances,
    resetCookingAppliance,
    setApplianceToBeRemoved,
    selectedCooktops,
    selectedRanges,
    setShowRemoveAllAppliancesModal,
    setShowRemoveApplianceModal,
    showRemoveAllAppliancesModal,
    showRemoveApplianceModal,
    totalCookingAppliancesAmount,
    updateQuantityHandler,
  };
};
